import { type ActorRefFrom, enqueueActions, type MachineImplementationsFrom, setup } from 'xstate5';

import { customizationMachine } from '@sg/shared-fe-features-customization';

import { BundleMachineDelays } from './bundle.machine.delays';
import {
  createProductAdds,
  getActorSelectionParams,
  getAddTimeoutId,
  getAddToBagSummary,
  getCustomizationInput,
  getFreeAddSlotCount,
  getTimedOutSelectionParams,
} from './helpers';
import { Actions, Guards } from './setup';
import { type AddToBagSummary, type Context, type Event, type Input } from './types';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actors: {
    customization: customizationMachine,
  },
  delays: BundleMachineDelays,
  guards: {
    checkIfAllProductsReported: Guards.checkIfAllProductsReported,
    checkIfCanSelectMore: Guards.checkIfCanSelectMore,
    checkIfHasAddedProducts: Guards.checkIfHasAddedProducts,
    checkIfHasPendingProduct: Guards.checkIfHasPendingProduct,
    checkIfHasSelectedProducts: Guards.checkIfHasSelectedProducts,
    checkIfProductInFlight: Guards.checkIfProductInFlight,
    checkIfSelectedProductActor: Guards.checkIfSelectedProductActor,
  },
  actions: {
    //
    // ─── Internal Actions ────────────────────────────────────────────────

    clearPendingProduct: Actions.clearPendingProduct,
    confirmPendingProduct: Actions.confirmPendingProduct,
    removeSelectedProduct: Actions.removeSelectedProduct,
    restartProductCustomization: Actions.restartProductCustomization,
    settleAddedProduct: Actions.settleAddedProduct,
    settleFailedProduct: Actions.settleFailedProduct,
    startProductAdds: Actions.startProductAdds,
    stopProductCustomization: Actions.stopProductCustomization,
    storeOutOfStockIngredients: Actions.storeOutOfStockIngredients,
    storePendingProduct: Actions.storePendingProduct,

    // ─── Product Actions ─────────────────────────────────────────────────

    spawnProductCustomization: enqueueActions((args) => {
      const { context, enqueue } = args;

      const confirmedProduct = context.selectedProducts.at(-1);

      if (!confirmedProduct) return;

      enqueue.spawnChild('customization', {
        id: confirmedProduct.selectionId,
        input: getCustomizationInput({ context, selectedProduct: confirmedProduct }),
      });
    }),
    requestProductAdds: enqueueActions((args) => {
      const { context, enqueue } = args;

      const { productAdds } = context;
      const selectionIds = productAdds.queue.slice(0, getFreeAddSlotCount(productAdds));

      if (selectionIds.length === 0) return;

      enqueue.assign({
        productAdds: {
          ...productAdds,
          queue: productAdds.queue.slice(selectionIds.length),
          inFlight: [...productAdds.inFlight, ...selectionIds],
        },
      });

      for (const selectionId of selectionIds) {
        enqueue.sendTo(selectionId, { type: 'customization.add-to-bag' });
        enqueue.raise(
          { type: 'PRODUCT_TIMED_OUT', selectionId },
          { id: getAddTimeoutId(selectionId), delay: 'PRODUCT_REPORT_TIMEOUT' },
        );
      }
    }),

    // ─── External Actions ────────────────────────────────────────────────

    onProductConfirmed: () => {},
    onAddedToBag: (_args: unknown, _summary: AddToBagSummary) => {},
    onFailedToAddToBag: () => {},
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const bundleMachine = model.createMachine({
  initial: 'ready',
  context: ({ input }) => ({
    restaurantSlug: input.restaurantSlug,
    isDelivery: input.isDelivery,
    ingredientAssetVersion: input.ingredientAssetVersion,
    isUpsellPricingEnabled: input.isUpsellPricingEnabled,
    selectedProducts: [],
    pendingProduct: undefined,
    nextSelectionId: 0,
    productAdds: createProductAdds([]),
  }),
  on: {
    'customization.out-of-stock-ingredients-found': {
      guard: { type: 'checkIfSelectedProductActor', params: getActorSelectionParams },
      actions: 'storeOutOfStockIngredients',
    },

    // NOTE: Otherwise a crashed customization machine takes the bundle down with it. It is not
    //       restarted here, since a crash caused by the product data would repeat forever.
    //       Its next "add" times out instead, which replaces it.
    'xstate.error.actor.*': {
      guard: { type: 'checkIfSelectedProductActor', params: getActorSelectionParams },
    },
  },
  states: {
    ready: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            OPEN_SELECT: {
              guard: 'checkIfCanSelectMore',
              target: 'selecting',
            },
            REMOVE_SELECTED: {
              actions: ['stopProductCustomization', 'removeSelectedProduct'],
            },
            ADD_TO_BAG: {
              guard: 'checkIfHasSelectedProducts',
              target: 'adding',
              actions: 'startProductAdds',
            },
          },
        },
        selecting: {
          on: {
            SELECT: {
              actions: 'storePendingProduct',
            },
            CONFIRM_SELECT: {
              guard: 'checkIfHasPendingProduct',
              target: 'idle',
              actions: ['confirmPendingProduct', 'spawnProductCustomization', 'onProductConfirmed'],
            },
            CLOSE_SELECT: {
              target: 'idle',
              actions: 'clearPendingProduct',
            },
          },
        },
        adding: {
          initial: 'products',
          states: {
            products: {
              entry: 'requestProductAdds',
              always: {
                guard: 'checkIfAllProductsReported',
                target: 'done',
              },
              on: {
                'customization.added-to-bag': {
                  guard: { type: 'checkIfProductInFlight', params: getActorSelectionParams },
                  actions: [
                    { type: 'settleAddedProduct', params: getActorSelectionParams },
                    'requestProductAdds',
                  ],
                },
                'customization.failed-to-add-to-bag': {
                  guard: { type: 'checkIfProductInFlight', params: getActorSelectionParams },
                  actions: [
                    { type: 'settleFailedProduct', params: getActorSelectionParams },
                    'requestProductAdds',
                  ],
                },
                PRODUCT_TIMED_OUT: {
                  guard: { type: 'checkIfProductInFlight', params: getTimedOutSelectionParams },
                  actions: [
                    { type: 'settleFailedProduct', params: getTimedOutSelectionParams },
                    { type: 'restartProductCustomization', params: getTimedOutSelectionParams },
                    'requestProductAdds',
                  ],
                },
                'xstate.error.actor.*': {
                  guard: { type: 'checkIfProductInFlight', params: getActorSelectionParams },
                  actions: [
                    { type: 'settleFailedProduct', params: getActorSelectionParams },
                    { type: 'restartProductCustomization', params: getActorSelectionParams },
                    'requestProductAdds',
                  ],
                },
              },
            },
            done: {
              type: 'final',
            },
          },
          onDone: [
            {
              guard: 'checkIfHasAddedProducts',
              target: 'added',
            },
            {
              target: 'idle',
              actions: 'onFailedToAddToBag',
            },
          ],
        },
        added: {
          description: 'Holds the finished add briefly, so it shows before the bundle closes.',
          after: {
            ADDED_CONFIRMATION_DURATION: {
              target: 'closing',
              actions: {
                type: 'onAddedToBag',
                params: (args) => getAddToBagSummary(args.context),
              },
            },
          },
        },
        closing: {
          description: 'Stays locked while the bundle closes.',
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type BundleMachineActorRef = ActorRefFrom<typeof bundleMachine>;
export type BundleMachineImplementations = MachineImplementationsFrom<typeof bundleMachine>;
